import React, { useCallback, useMemo, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { StyleSheet } from "~/ui/primitives"
import styled, { useTheme } from "~/ui/styled"
import { useSafeAreaInsets } from "~/ui/safe-area"
import { useFocusEffect } from "@react-navigation/native"
import { Flex, ColumnFlex, StartFlex, CenterFlex, BetweenFlex, Button, Text, IconFont } from "~components/El"
import { ClearCacheDataScreenProps } from "./types"
import { useModal } from "~hooks/useModal"
import {
  clearCacheCategories,
  globalToast,
  globalSpinner,
  measureAppCaches,
} from "~utils"

interface ClearCacheListItem {
    id: number
    name: string
    description: string
    size: number
    icon: string
}

const Container = styled.ScrollView`
  flex-direction: column;
  flex: 1;
  padding: 0 12px;
`
const tips = [
    "清理缓存可能导致应用重新加载数据",
    "清理图片缓存会重新下载聊天图片",
    "日志文件清理后无法恢复，建议保留用于问题排查",
    "清理操作不可撤销，请谨慎选择"
]

const defaultCacheItems: ClearCacheListItem[] = [
    {
        id: 1,
        name: "应用缓存",
        description: "临时文件和应用数据",
        size: 0,
        icon: "yingyonghuancun",
    },
    {
        id: 2,
        name: "图片缓存",
        description: "聊天中的图片和头像缓存",
        size: 0,
        icon: "zhaopian",
    },
    {
        id: 3,
        name: "日志文件",
        description: "应用运行日志和错误记录",
        size: 0,
        icon: "wyhc",
    }
]

type ListContentProps = {
    data?: ClearCacheListItem[]
    selecteds?: ClearCacheListItem[]
    onClick: (item: ClearCacheListItem | null) => void
}

const ListContent = ({ data = [], selecteds = [], onClick }: ListContentProps) => {
  const theme = useTheme()
  return (
    <ColumnFlex width="100%">
        <BetweenFlex width="100%">
            <ColumnFlex padding="12px 0">
                <Text padding="3px 0" color={theme.colors.text} fontSize={theme.fontSizes.md} fontWeight="600">清理项目</Text>
                <Text padding="3px 0" color={theme.colors.secondary} fontSize={theme.fontSizes.sm}>选择要清理的数据类型</Text>
            </ColumnFlex>
            {selecteds.length > 0 && (
                <Button
                    padding="6px 12px"
                    borderRadius="4px"
                    bg="#f7514d"
                    onClick={() => onClick(null)}
                >
                    <Text color={theme.colors.textLight} fontSize={theme.fontSizes.sm}>清理选中项({selecteds.length})</Text>
                </Button>
            )}
        </BetweenFlex>
        {data.map((item) => {
            const checked = selecteds.some((i) => i.id === item.id)
            return (
                <Button
                    key={item.id}
                    flexDirection="row"
                    width="100%"
                    padding="15px 0"
                    borderTopWidth={StyleSheet.hairlineWidth}
                    borderTopColor={theme.colors.border}
                    borderTopStyle="solid"
                    onClick={() => onClick(item)}
                >
                    <BetweenFlex width="100%">
                        <Flex flex={1}>
                            <CenterFlex>
                                <IconFont name={checked ? "cg" : "yuan"} size={15} color={checked ? theme.colors.primary : theme.colors.border} />
                            </CenterFlex>
                            <CenterFlex
                                width="36px"
                                height="36px"
                                ml="10px"
                                borderRadius="18px"
                                bg="rgba(49, 135, 226, 0.1)"
                            >
                                <IconFont name={item.icon} size={16} color="#3187e2" />
                            </CenterFlex>
                            <Flex flex={3} flexDirection="column" ml="10px">
                                <Text fontSize={theme.fontSizes.md} color={theme.colors.text} fontWeight="600">{item.name || ""}</Text>
                                <Text padding="3px 0" fontSize={theme.fontSizes.sm} color={theme.colors.secondary}>{item.description || ""}</Text>
                            </Flex>
                            
                        </Flex>
                        <Flex flexDirection="row" ml="10px" alignItems="center">
                            <Text fontSize={theme.fontSizes.md} color={theme.colors.text} mr="10px">{item.size}MB</Text>
                            <IconFont name="delete" size={14} color={item.size > 0 ? theme.colors.error : theme.colors.border} />
                        </Flex>
                    </BetweenFlex>
                </Button>
            )
        })}
    </ColumnFlex>
    
  )
}


const ClearCacheData: React.FC<ClearCacheDataScreenProps> = ({ route }) => {
    const theme = useTheme()
    const insets = useSafeAreaInsets()
    const { Dialog} = useModal()
    const [cacheData, setCacheData] = useState<ClearCacheListItem[]>(defaultCacheItems)
    const [totalOccupiedMb, setTotalOccupiedMb] = useState(0)
    const [clearedSessionMb, setClearedSessionMb] = useState(0)
    const [selecteds, setSelecteds] = useState<ClearCacheListItem[]>([])
    const firstFocus = useRef(true)

    const loadCacheSizes = useCallback(async (showSpinner: boolean) => {
        if (showSpinner) globalSpinner.show("加载中...")
        try {
            const m = await measureAppCaches()
            setTotalOccupiedMb(m.totalMb)
            setCacheData((prev) =>
                prev.map((row) => ({
                    ...row,
                    size: m.byId[row.id]?.mb ?? 0,
                })),
            )
        } catch (e: unknown) {
            globalToast.show(e instanceof Error ? e.message : "读取缓存失败")
        } finally {
            if (showSpinner) globalSpinner.close()
        }
    }, [])

    useFocusEffect(
        useCallback(() => {
            const showSpinner = firstFocus.current
            firstFocus.current = false
            void loadCacheSizes(showSpinner)
        }, [loadCacheSizes]),
    )

    const reclaimableMb = useMemo(() => {
        if (selecteds.length === 0) return totalOccupiedMb
        return Math.round(
            selecteds.reduce((sum, sel) => {
                const row = cacheData.find((c) => c.id === sel.id)
                return sum + (row?.size ?? 0)
            }, 0) * 10,
        ) / 10
    }, [selecteds, cacheData, totalOccupiedMb])

    const handlePress = useCallback(
        (item: ClearCacheListItem | null) => {
            if (item === null) {
                const toClear = [...selecteds]
                if (toClear.length === 0) return
                const ids = toClear.map((i) => i.id)
                Dialog.show({
                    title: "温馨提示",
                    content: `确定清理已选中的 ${toClear.length} 项缓存吗？清理后相关数据将重新加载。`,
                    loadingText: "清理中...",
                    onConfirm: async () => {
                        try {
                            const before = await measureAppCaches()
                            await clearCacheCategories(ids)
                            const after = await measureAppCaches()
                            const freedBytes = Math.max(0, before.totalBytes - after.totalBytes)
                            const freedMb =
                                Math.round((freedBytes / (1024 * 1024)) * 10) / 10
                            setClearedSessionMb((s) => s + freedMb)
                            setTotalOccupiedMb(after.totalMb)
                            setCacheData((prev) =>
                                prev.map((row) => ({
                                    ...row,
                                    size: after.byId[row.id]?.mb ?? 0,
                                })),
                            )
                            setSelecteds([])
                            globalToast.show(
                                freedMb > 0 ? `已释放约 ${freedMb}MB 空间` : "清理完成",
                            )
                        } catch (e: unknown) {
                            globalToast.show(e instanceof Error ? e.message : "清理失败")
                        } finally {
                        }
                    },
                })
                return
            }
            setSelecteds((prev) => {
                const exists = prev.some((i) => i.id === item.id)
                if (exists) return prev.filter((i) => i.id !== item.id)
                return [...prev, item]
            })
        },
        [Dialog, selecteds],
    )
    return (
        <Container style={{ marginBottom: insets.bottom}}>
            <Flex
                flexDirection="column"
                width="100%"
                mt="10px"
                padding="10px 12px"
                borderRadius="8px"
                bg={theme.colors.blockBackground}
            >
                <Text color={theme.colors.text} fontSize={theme.fontSizes.md} fontWeight="600">
                    <IconFont name="denglushebei" size={20} color={"#834f0c"} />&nbsp;&nbsp;储存空间
                </Text>
                <BetweenFlex
                    width="100%"
                    mt="10px"
                    gap="12px"
                >
                    <CenterFlex
                        flex={1}
                        flexDirection="column"
                        height="58px"
                        padding="10px"
                        borderRadius="8px"
                        bg="#ebf3fd"
                    >
                        <Text color="#82b1f2" fontSize={theme.fontSizes["2xl"]} fontWeight="600">{totalOccupiedMb}MB</Text>
                        <Text color="#82b1f2" fontSize={theme.fontSizes.md}>总占用</Text>
                    </CenterFlex>
                    <CenterFlex
                        flex={1}
                        flexDirection="column"
                        height="58px"
                        padding="10px"
                        borderRadius="8px"
                        bg="#fcebe3"
                    >
                        <Text color="#e14b1f" fontSize={theme.fontSizes["2xl"]} fontWeight="600">{reclaimableMb}MB</Text>
                        <Text color="#e14b1f" fontSize={theme.fontSizes.md} >可清理</Text>
                    </CenterFlex>
                    <CenterFlex
                        flex={1}
                        flexDirection="column"
                        height="58px"
                        padding="10px"
                        borderRadius="8px"
                        bg="#e2f8ed"
                    >
                        <Text color="#21bf6c" fontSize={theme.fontSizes["2xl"]} fontWeight="600">{clearedSessionMb}MB</Text>
                        <Text color="#21bf6c" fontSize={theme.fontSizes.md} >已清理</Text>
                    </CenterFlex>
                </BetweenFlex>
            </Flex>
            <ColumnFlex
                width="100%"
                mt="10px"
                padding="0 12px"
                borderRadius="8px"
                bg={theme.colors.blockBackground}
            >
                <ListContent data={cacheData} selecteds={selecteds} onClick={handlePress}/>
            </ColumnFlex>
            <Flex
                flexDirection="column"
                width="100%"
                mt="10px"
                padding="10px 12px"
                borderRadius="8px"
                bg="#fffbe9"
            >
                <Text color="#8e331b" fontSize={theme.fontSizes.md} fontWeight="600">
                <IconFont name="jinggao" size={14} color={theme.colors.error} />&nbsp;&nbsp;淸理须知
                </Text>
                <Flex
                    flexDirection="column"
                    width="100%"
                    mt="10px"
                    pl="20px"
                    >
                    {tips.map((text, index) => (
                        <StartFlex key={index} >
                            <Text color="#8e331b" fontSize={theme.fontSizes.sm} lineHeight="18px">
                                {text}
                            </Text>
                        </StartFlex>
                    ))}
                </Flex>
            </Flex>
        </Container>
      )
}

export default observer(ClearCacheData)
